import { ActionIcon, Badge, Button, Card, Group, NumberInput, Select, Stack, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlayerPlay, IconSparkles, IconTrash } from "@tabler/icons-react";
import { type JSX, useState } from "react";
import { useMutation } from "urql";
import { useSiteConfig } from "../SiteConfig";
import {
    type TtsBlockDeleteMutation,
    type TtsBlockDeleteMutationVariables,
    type TtsBlockGenerateMutation,
    type TtsBlockGenerateMutationVariables,
    type TtsBlockUpdateMutation,
    type TtsBlockUpdateMutationVariables,
    type TtsProjectQuery,
    ttsBlockDeleteMutation,
    ttsBlockGenerateMutation,
    ttsBlockUpdateMutation,
} from "./queries";
import { CUSTOM_SILENCE_OPTION, SILENCE_OPTIONS, clampSilence } from "./silenceOptions";
import classes from "./TtsBlockCard.module.css";

type BlockType = NonNullable<TtsProjectQuery["ttsProject"]>["blocks"]["edges"][number]["node"];

interface IProps {
    block: BlockType;
    onUpdate: () => void;
    isProcessing: boolean;
}

function TtsBlockCard({ block, onUpdate, isProcessing }: IProps): JSX.Element {
    const config = useSiteConfig();
    const initialSilenceAfter = String(block.silenceAfter);
    const hasPresetSilenceAfterOption = SILENCE_OPTIONS.some((option) => option.value === initialSilenceAfter);
    const initialSilenceAfterOption = hasPresetSilenceAfterOption ? initialSilenceAfter : CUSTOM_SILENCE_OPTION;
    const [silenceAfterOption, setSilenceAfterOption] = useState<string>(initialSilenceAfterOption);
    const [customSilenceAfter, setCustomSilenceAfter] = useState<string>(initialSilenceAfter);

    const [, executeUpdate] = useMutation<TtsBlockUpdateMutation, TtsBlockUpdateMutationVariables>(
        ttsBlockUpdateMutation,
    );
    const [deleteResult, executeDelete] = useMutation<TtsBlockDeleteMutation, TtsBlockDeleteMutationVariables>(
        ttsBlockDeleteMutation,
    );
    const [generateResult, executeGenerate] = useMutation<TtsBlockGenerateMutation, TtsBlockGenerateMutationVariables>(
        ttsBlockGenerateMutation,
    );

    const form = useForm({
        initialValues: {
            text: block.text,
            voice: block.voice,
            instructions: block.instructions ?? "",
            silenceAfter: initialSilenceAfter,
        },
    });

    const saveChanges = useDebouncedCallback(async (values: typeof form.values) => {
        const res = await executeUpdate({
            id: block.id,
            text: values.text,
            voice: values.voice,
            instructions: values.instructions || null,
            silenceAfter: parseFloat(values.silenceAfter),
        });
        if (res.error) {
            notifications.show({ title: "Failed to update block", message: res.error.message });
        } else {
            onUpdate();
        }
    }, 500);

    const handleChange = (field: keyof typeof form.values) => (value: string | null) => {
        form.setFieldValue(field, value ?? "");
        saveChanges({ ...form.values, [field]: value ?? "" });
    };

    const handleSilenceAfterSelectChange = (value: string | null) => {
        const nextValue = value ?? "";
        if (nextValue === CUSTOM_SILENCE_OPTION) {
            setSilenceAfterOption(CUSTOM_SILENCE_OPTION);
            setCustomSilenceAfter(form.values.silenceAfter);
            return;
        }
        if (nextValue === "") {
            return;
        }
        setSilenceAfterOption(nextValue);
        setCustomSilenceAfter(nextValue);
        handleChange("silenceAfter")(nextValue);
    };

    const handleCustomSilenceAfterChange = (value: string | number) => {
        const clamped = clampSilence(value);
        if (clamped === null) {
            setCustomSilenceAfter(value === undefined || value === null ? "" : String(value));
            return;
        }
        setSilenceAfterOption(CUSTOM_SILENCE_OPTION);
        setCustomSilenceAfter(clamped);
        const newValues = { ...form.values, silenceAfter: clamped };
        form.setFieldValue("silenceAfter", clamped);
        saveChanges(newValues);
    };

    const onDelete = async () => {
        const res = await executeDelete({ id: block.id });
        if (res.error) {
            notifications.show({ title: "Failed to delete block", message: res.error.message });
        } else {
            onUpdate();
        }
    };

    const onGenerate = async () => {
        const res = await executeGenerate({ id: block.id });
        if (res.error) {
            notifications.show({ title: "Failed to generate audio", message: res.error.message });
        } else {
            notifications.show({ title: "Generation started", message: "Audio generation started in background" });
            onUpdate();
        }
    };

    const playAudio = () => {
        if (block.file?.src) {
            const audio = new Audio(block.file.src);
            audio.play();
        }
    };

    return (
        <Card withBorder className={classes.card}>
            <Stack gap="xs">
                <Group justify="flex-end" gap="xs">
                    {block.audioStale || !block.file ? (
                        <Badge color="yellow" size="sm">
                            Needs regeneration
                        </Badge>
                    ) : (
                        <Badge color="green" size="sm">
                            Ready
                        </Badge>
                    )}
                    <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconSparkles size={16} />}
                        onClick={onGenerate}
                        loading={generateResult.fetching}
                        disabled={isProcessing || generateResult.fetching}
                    >
                        Generate audio
                    </Button>
                    <ActionIcon
                        variant="light"
                        onClick={playAudio}
                        disabled={!block.file || block.audioStale}
                        title="Play audio"
                    >
                        <IconPlayerPlay size={16} />
                    </ActionIcon>
                    <ActionIcon
                        color="red"
                        variant="light"
                        onClick={onDelete}
                        disabled={deleteResult.fetching}
                        title="Delete block"
                    >
                        <IconTrash size={16} />
                    </ActionIcon>
                </Group>
                <Textarea
                    value={form.values.text}
                    onChange={(e) => handleChange("text")(e.currentTarget.value)}
                    autosize
                    minRows={2}
                    maxRows={10}
                />
                <Group grow>
                    <Select
                        label="Voice"
                        size="xs"
                        data={config.ttsVoices.map((v) => ({ value: v, label: v }))}
                        value={form.values.voice}
                        onChange={handleChange("voice")}
                    />
                    <Select
                        label="Silence After"
                        size="xs"
                        data={[...SILENCE_OPTIONS, { value: CUSTOM_SILENCE_OPTION, label: "Custom" }]}
                        value={silenceAfterOption}
                        onChange={handleSilenceAfterSelectChange}
                    />
                    {silenceAfterOption === CUSTOM_SILENCE_OPTION ? (
                        <NumberInput
                            label="Custom silence (seconds)"
                            size="xs"
                            value={customSilenceAfter}
                            onChange={handleCustomSilenceAfterChange}
                            min={0}
                            max={9.99}
                            step={0.1}
                            decimalScale={2}
                            allowNegative={false}
                        />
                    ) : null}
                </Group>
                <Textarea
                    label="Instructions"
                    size="xs"
                    value={form.values.instructions}
                    onChange={(e) => handleChange("instructions")(e.currentTarget.value)}
                    autosize
                    minRows={1}
                    maxRows={3}
                    placeholder="Optional: accent, tone, speed, emotional range..."
                />
            </Stack>
        </Card>
    );
}

export default TtsBlockCard;
